import { observable, action } from 'mobx'
import { PaginationUtil } from '../../../../utilities/pagination'
import { History } from 'history'
import { ProductModels } from '@getgreenline/products'
import { EOptionalFilters } from '.'

interface IProductFilter {
  selectedOptionalFilters: EOptionalFilters[] | undefined
  searchQuery: string
  selectedSupplierId: number | undefined
  selectedCategoryIds: string[] | undefined
  selectedStrainTypes: ProductModels.EProductStrainType[] | undefined
  selectedTagIds: string[] | undefined
  isActive: boolean | undefined
  isDeleted: boolean | undefined
}

export class ProductFilter extends PaginationUtil {
  defaultProductLimit = 20
  maximumProductLimit = 50

  private initialFilters = {
    selectedOptionalFilters: undefined,
    searchQuery: '',
    selectedSupplierId: undefined,
    selectedCategoryIds: undefined,
    selectedStrainTypes: undefined,
    selectedTagIds: undefined,
    isActive: true,
    isDeleted: false,
  }

  @observable
  productFilter: IProductFilter

  constructor(history: History) {
    super(history, false)

    this.productFilter = this.initialFilters

    const {
      selectedOptionalFilters,
      searchQuery,
      selectedSupplierId,
      selectedCategoryIds,
      selectedStrainTypes,
      selectedTagIds,
      isActive,
      offset,
      limit,
      isDeleted,
    } = history.getCurrentLocation().query

    if (selectedOptionalFilters) {
      this.setSelectedOptionalFilters(selectedOptionalFilters)
    }
    if (searchQuery) {
      this.setSearchQuery(searchQuery)
    }
    if (selectedSupplierId) {
      this.setSelectedSupplierId(parseInt(selectedSupplierId))
    }
    if (selectedCategoryIds) {
      this.setSelectedCategoryIds(selectedCategoryIds)
    }
    if (selectedStrainTypes) {
      this.setSelectedStrainTypes(selectedStrainTypes)
    }
    if (selectedTagIds) {
      this.setSelectedTagIds(selectedTagIds)
    }
    if (isActive) {
      this.setIsActive(isActive === 'true')
    }
    if (isDeleted) {
      this.setIsDeleted(isDeleted === 'true')
    }

    const searchLimit =
      limit && parseInt(limit) <= this.maximumProductLimit
        ? parseInt(limit)
        : this.defaultProductLimit

    this.setLimit(searchLimit)
    this.setOffset(offset ? parseInt(offset) : 0)
    this.applyFilters()
  }

  applyFilters() {
    this.apply(this.productFilter)
  }

  resetDisabled() {
    const {
      selectedOptionalFilters,
      searchQuery,
      selectedCategoryIds,
      selectedSupplierId,
      isActive,
      isDeleted,
      selectedStrainTypes,
      selectedTagIds,
    } = this.productFilter

    return (
      !selectedOptionalFilters &&
      !searchQuery &&
      !selectedSupplierId &&
      !selectedCategoryIds &&
      isActive &&
      !isDeleted &&
      !selectedStrainTypes &&
      !selectedTagIds
    )
  }

  @action
  setIsActive(isActive: boolean) {
    this.productFilter.isDeleted = undefined
    this.productFilter.isActive = isActive
  }

  @action
  setIsDeleted(isDeleted: boolean) {
    this.productFilter.isActive = undefined
    this.productFilter.isDeleted = isDeleted
  }

  @action
  setSearchQuery(searchQuery: string) {
    this.productFilter.searchQuery = searchQuery
  }

  @action
  setSelectedSupplierId(selectedSupplierId: number | undefined) {
    this.productFilter.selectedSupplierId = selectedSupplierId
  }

  @action
  setSelectedCategoryIds(selectedCategoryIds: string | string[] | undefined) {
    if (!selectedCategoryIds) {
      this.productFilter.selectedCategoryIds = undefined
    } else {
      this.productFilter.selectedCategoryIds = Array.isArray(selectedCategoryIds)
        ? selectedCategoryIds
        : [selectedCategoryIds]
    }
  }

  @action
  setSelectedTagIds(selectedTagIds: string | string[] | undefined) {
    if (!selectedTagIds) {
      this.productFilter.selectedTagIds = undefined
    } else {
      this.productFilter.selectedTagIds = Array.isArray(selectedTagIds)
        ? selectedTagIds
        : [selectedTagIds]
    }
  }

  @action
  setSelectedStrainTypes(
    selectedStrainTypes:
      | ProductModels.EProductStrainType
      | ProductModels.EProductStrainType[]
      | undefined,
  ) {
    if (!selectedStrainTypes) {
      this.productFilter.selectedStrainTypes = undefined
    } else {
      this.productFilter.selectedStrainTypes = Array.isArray(selectedStrainTypes)
        ? selectedStrainTypes
        : [selectedStrainTypes]
    }
  }

  @action
  setSelectedOptionalFilters(
    selectedOptionalFilters: EOptionalFilters | EOptionalFilters[] | undefined,
  ) {
    if (!selectedOptionalFilters) {
      this.productFilter.selectedOptionalFilters = undefined
    } else {
      this.productFilter.selectedOptionalFilters = Array.isArray(selectedOptionalFilters)
        ? selectedOptionalFilters
        : [selectedOptionalFilters]
    }
  }

  @action
  resetOptionalFilters() {
    this.productFilter.selectedStrainTypes = undefined
    this.productFilter.selectedTagIds = undefined
  }

  @action
  reset() {
    this.productFilter = this.initialFilters
  }
}
